import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * KioskViewport - Full-width kiosk canvas that fills the physical screen
 */
export function KioskViewport({ children }) {
  return (
    <div style={{ 
      width: '100vw', 
      height: '100vh', 
      overflow: 'hidden', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#0d2137'
    }}>
      {children}
    </div>
  );
}

/**
 * =====================================================
 * CENTRAL KIOSK SPEECH CONTROLLER
 * =====================================================
 * Single point of control for all kiosk TTS operations.
 * Ensures no overlapping speech and proper sequencing.
 */

const AUDIO_UNLOCK_KEY = 'parkwise_audio_unlocked';

// Global speech state
let currentRouteId = null;
let isSpeaking = false;

/**
 * Check if audio is unlocked (user has interacted)
 */
export function isAudioUnlocked() {
  const unlocked = sessionStorage.getItem(AUDIO_UNLOCK_KEY) === 'true';
  return unlocked;
}

/**
 * Set audio as unlocked
 */
export function setAudioUnlocked() {
  sessionStorage.setItem(AUDIO_UNLOCK_KEY, 'true');
  console.log('[TTS] AUDIO_UNLOCK_SET: true');
}

/**
 * Cancel any ongoing speech immediately
 */
export function cancelSpeech(reason = 'unknown') {
  if (window.speechSynthesis) {
    console.log('[TTS] ROUTE_CHANGE_CANCEL_PREVIOUS_SPEECH:', reason);
    window.speechSynthesis.cancel();
    isSpeaking = false;
  }
}

/**
 * Get best available female UK English voice
 */
function getBestFemaleVoice() {
  const voices = window.speechSynthesis.getVoices();
  const ukFemaleVoices = ['Google UK English Female', 'Microsoft Hazel Desktop', 'Microsoft Hazel', 'Hazel', 'Kate', 'Serena', 'Martha'];
  
  for (const voiceName of ukFemaleVoices) {
    const found = voices.find(v => v.name.includes(voiceName) || (v.lang === 'en-GB' && v.name.toLowerCase().includes('female')));
    if (found) return found;
  }
  
  const anyUKVoice = voices.find(v => v.lang === 'en-GB' && !v.name.toLowerCase().includes('male') && !v.name.includes('Daniel'));
  if (anyUKVoice) return anyUKVoice;
  
  const usFemale = voices.find(v => v.name.includes('Samantha') || v.name.includes('Victoria') || v.name.includes('Karen'));
  if (usFemale) return usFemale;
  
  return voices.find(v => v.lang.startsWith('en')) || null;
}

/**
 * Core speak function - speaks a message
 */
function doSpeak(message, routeId) {
  if (!window.speechSynthesis || !message) {
    console.log('[TTS] doSpeak aborted: no speechSynthesis or message');
    return false;
  }

  // If route changed while waiting, abort
  if (routeId && currentRouteId !== routeId) {
    console.log('[TTS] doSpeak aborted: route changed from', routeId, 'to', currentRouteId);
    return false;
  }

  const utterance = new SpeechSynthesisUtterance(message);
  utterance.rate = 0.95;
  utterance.pitch = 1.1;
  utterance.volume = 1.0;
  utterance.lang = 'en-GB';
  
  const voice = getBestFemaleVoice();
  if (voice) {
    utterance.voice = voice;
  }
  
  utterance.onstart = () => {
    console.log('[TTS] AUDIO_SPEAK_STARTED');
    isSpeaking = true;
  };
  
  utterance.onend = () => {
    console.log('[TTS] AUDIO_SPEAK_ENDED');
    isSpeaking = false;
  };
  
  utterance.onerror = (e) => {
    console.log('[TTS] AUDIO_SPEAK_ERROR:', e.error);
    isSpeaking = false;
  };
  
  console.log('[TTS] NEW_ROUTE_SPEAK_CALLED:', message.substring(0, 60) + '...');
  window.speechSynthesis.speak(utterance);
  return true;
}

/**
 * Speak text (exported utility)
 */
export function speakText(message, onError) {
  if (!window.speechSynthesis) {
    if (onError) onError();
    return false;
  }
  
  // Cancel any ongoing speech first
  cancelSpeech('speakText manual call');
  
  try {
    return doSpeak(message, null);
  } catch (err) {
    console.log('[TTS] speakText error:', err);
    if (onError) onError();
    return false;
  }
}

/**
 * Unlock audio on first user touch - call this on Home/Attract screen
 */
export function useAudioUnlock() {
  const [unlocked, setUnlocked] = useState(() => {
    return sessionStorage.getItem(AUDIO_UNLOCK_KEY) === 'true';
  });

  useEffect(() => {
    if (unlocked) return;

    const unlock = () => {
      console.log('[TTS] AUDIO_UNLOCK_SET: User interaction detected');
      // Prime speech synthesis with empty utterance
      if (window.speechSynthesis) {
        const primer = new SpeechSynthesisUtterance('');
        primer.volume = 0;
        window.speechSynthesis.speak(primer);
      }
      setAudioUnlocked();
      setUnlocked(true);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('click', unlock);
    };

    document.addEventListener('touchstart', unlock, { once: true, passive: true });
    document.addEventListener('click', unlock, { once: true });

    return () => {
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('click', unlock);
    };
  }, [unlocked]);

  return unlocked;
}

/**
 * =====================================================
 * useKioskSpeech - Central hook for screen-level speech
 * =====================================================
 * 
 * Usage: const { speak, needsInteraction } = useKioskSpeech('ScreenName', 'Your guidance message');
 * 
 * Behavior:
 * 1. On mount: Cancels any previous screen's speech
 * 2. After brief delay: Speaks this screen's message (if audio unlocked)
 * 3. On unmount: Cancels speech (in case user navigates away mid-speech)
 */
export function useKioskSpeech(routeName, message, enabled = true) {
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const [hasSpoken, setHasSpoken] = useState(false);
  const routeIdRef = useRef(`${routeName}_${Date.now()}`);
  const mountedRef = useRef(true);

  // Manual speak trigger
  const speak = useCallback(() => {
    if (!message || !window.speechSynthesis) return;
    
    // Ensure audio is unlocked
    setAudioUnlocked();
    setNeedsInteraction(false);
    
    // Cancel any existing speech first
    cancelSpeech('manual speak trigger');
    
    // Speak after brief delay
    setTimeout(() => {
      if (mountedRef.current) {
        doSpeak(message, routeIdRef.current);
        setHasSpoken(true);
      }
    }, 100);
  }, [message]);

  useEffect(() => {
    mountedRef.current = true;
    const myRouteId = routeIdRef.current;
    
    // Step 1: Cancel previous screen's speech immediately
    cancelSpeech(`new route: ${routeName}`);
    
    // Step 2: Register this as the current route
    currentRouteId = myRouteId;
    console.log('[TTS] NEW_ROUTE_MOUNTED:', routeName);
    
    if (!enabled || !message) {
      console.log('[TTS] Speech disabled or no message for', routeName);
      return;
    }
    
    console.log('[TTS] NEW_ROUTE_MESSAGE_SELECTED:', message.substring(0, 60) + '...');
    
    // Step 3: Check if audio is unlocked
    const audioUnlocked = isAudioUnlocked();
    console.log('[TTS] AUDIO_UNLOCK_READ:', audioUnlocked);
    
    if (!audioUnlocked) {
      console.log('[TTS] Audio not unlocked, user interaction required');
      setNeedsInteraction(true);
      return;
    }
    
    // Step 4: Wait for voices to load, then speak
    let retryCount = 0;
    const maxRetries = 30; // 3 seconds max
    let timeoutId = null;
    
    const trySpeak = () => {
      // Check if still mounted and still the current route
      if (!mountedRef.current || currentRouteId !== myRouteId) {
        console.log('[TTS] Aborting speech: unmounted or route changed');
        return;
      }
      
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        doSpeak(message, myRouteId);
        setHasSpoken(true);
      } else if (retryCount < maxRetries) {
        retryCount++;
        timeoutId = setTimeout(trySpeak, 100);
      } else {
        console.log('[TTS] Max retries reached, no voices available');
      }
    };
    
    // Start speaking after a brief delay (allow React to finish rendering)
    timeoutId = setTimeout(trySpeak, 200);
    
    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      if (timeoutId) clearTimeout(timeoutId);
      // Cancel speech when leaving this screen
      if (currentRouteId === myRouteId) {
        cancelSpeech(`leaving route: ${routeName}`);
      }
    };
  }, [routeName, message, enabled]);

  return { needsInteraction, hasSpoken, speak };
}

/**
 * Legacy useAutoSpeak - now wraps useKioskSpeech
 * Kept for backward compatibility but uses the central controller
 */
export function useAutoSpeak(message, enabled = true) {
  const { needsInteraction, hasSpoken, speak } = useKioskSpeech('legacy', message, enabled);
  
  return { 
    needsInteraction, 
    hasSpoken, 
    triggerSpeak: speak,
    speak 
  };
}

export default KioskViewport;
